/**
 * ResponsiveProTable — renders ProTable on desktop, MobileCardList on mobile.
 *
 * Drop-in replacement for @ant-design/pro-table.  Reads isPC from Redux internally.
 * Only the import line needs to change:
 *
 *   // before
 *   import ProTable from "@ant-design/pro-table";
 *   // after
 *   import ResponsiveProTable from "@components/ResponsiveProTable";
 *
 * JSX stays the same: <ResponsiveProTable request={fn} columns={cols} ... />
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Drawer, Empty, Tooltip } from "antd";
import { CheckOutlined, SortAscendingOutlined } from "@ant-design/icons";
import ProTable from "@ant-design/pro-table";
import MobileCardList from "../MobileCardList";
import { getNodeText } from "../ResponsivePicker/utils";

/* ========================================================
   Hook – call ProTable's request function and manage state
   ======================================================== */
function useProTableData(request, options = {}) {
  const {
    enabled = true,
    pageSize: initialPageSize = 20,
    infinite = false,
    postData,
    sort = {},
  } = options;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = initialPageSize;
  const requestRef = useRef(request);
  requestRef.current = request;
  const postDataRef = useRef(postData);
  postDataRef.current = postData;
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const sortKey = JSON.stringify(sort || {});

  const fetchData = useCallback(
    async (page, size = pageSize, append = false) => {
      if (!enabled || !requestRef.current) return;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setLoadMoreError(false);
      try {
        const result = await requestRef.current(
          {
            current: page,
            pageSize: size,
          },
          sortRef.current || {},
          {}
        );
        if (result) {
          const nextData = result.data || [];
          setData((prev) => {
            const mergedData = append ? [...prev, ...nextData] : nextData;
            if (typeof postDataRef.current === "function") {
              return postDataRef.current(mergedData) || mergedData;
            }
            return mergedData;
          });
          setTotal(result.total || 0);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("ResponsiveProTable fetch error:", err);
        if (append) {
          setLoadMoreError(true);
        }
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [enabled, pageSize]
  );

  useEffect(() => {
    if (!enabled) return;
    setData([]);
    setTotal(0);
    setCurrentPage(1);
    fetchData(1, pageSize);
  }, [enabled, fetchData, pageSize, sortKey]);

  const reload = useCallback(() => {
    setData([]);
    setCurrentPage(1);
    return fetchData(1, pageSize);
  }, [fetchData, pageSize]);

  const refresh = useCallback(() => {
    if (infinite) {
      return reload();
    }
    return fetchData(currentPage, pageSize);
  }, [fetchData, currentPage, pageSize, infinite, reload]);

  const changePage = useCallback(
    (page) => fetchData(page, pageSize),
    [fetchData, pageSize]
  );

  const loadMore = useCallback(() => {
    if (loading || loadingMore || data.length >= total) {
      return undefined;
    }
    return fetchData(currentPage + 1, pageSize, true);
  }, [loading, loadingMore, data.length, total, fetchData, currentPage, pageSize]);

  return {
    data,
    loading,
    loadingMore,
    loadMoreError,
    total,
    currentPage,
    pageSize,
    refresh,
    reload,
    changePage,
    loadMore,
    hasMore: data.length < total,
  };
}

/* ========================================================
   Component
   ======================================================== */
const ResponsiveProTable = (props) => {
  const isPC = useSelector((s) => s.App?.isPC ?? true);
  const [mobileSort, setMobileSort] = useState({});
  const [mobileSortDrawerOpen, setMobileSortDrawerOpen] = useState(false);
  const {
    request, columns, rowKey, rowSelection, pagination,
    expandable, onRow, search, toolBarRender, options, actionRef, postData,
    mobileRowActions, mobileInfiniteScroll = false, mobilePageSize = 20,
    hideMobileColumnActions = false,
    hideMobileTableExpand = false,
    mobileDetailButtonText,
    mobileCardHeaderRender,
    mobileTitleOnClick,
    mobileExpandedExcludeKeys,
    mobileExpandedIncludeKeys,
    ...rest
  } = props;

  const mobileColumns = useMemo(() => {
    if (!columns) return [];
    return columns.filter((col) =>
      col.valueType !== "index" && col.valueType !== "indexBorder"
    );
  }, [columns]);

  const mobileSortableColumns = useMemo(
    () =>
      mobileColumns
        .filter((col) => col.sorter && col.title)
        .map((col) => {
          const rawField = col.dataIndex ?? col.key;
          const field = Array.isArray(rawField) ? rawField.join(".") : rawField;
          if (field == null || field === "") return null;
          return {
            field: String(field),
            label: getNodeText(col.title) || String(field),
          };
        })
        .filter(Boolean),
    [mobileColumns]
  );

  const activeMobileSortField = Object.keys(mobileSort || {})[0];
  const activeMobileSortOrder = activeMobileSortField ? mobileSort[activeMobileSortField] : undefined;
  const hasActiveMobileSort = Boolean(activeMobileSortField && activeMobileSortOrder);

  const handleMobileSortSelect = useCallback((field, order) => {
    setMobileSort({ [field]: order });
    setMobileSortDrawerOpen(false);
  }, []);

  const handleMobileSortReset = useCallback(() => {
    setMobileSort({});
    setMobileSortDrawerOpen(false);
  }, []);

  // ---- mobile data management ----
  const {
    data,
    loading,
    loadingMore,
    loadMoreError,
    total,
    currentPage,
    pageSize,
    refresh,
    reload,
    changePage,
    loadMore,
    hasMore,
  } = useProTableData(request, {
    enabled: !isPC,
    pageSize: mobilePageSize,
    infinite: mobileInfiniteScroll,
    postData,
    sort: mobileSort,
  });

  // Expose reload via actionRef on mobile
  const mobileActionRef = useMemo(
    () => ({
      reload,
      reset: reload,
      clearSelected: () => {
        if (rowSelection && typeof rowSelection.onChange === "function") {
          rowSelection.onChange([], []);
        }
      },
      startEditable: () => {},
      cancelEditable: () => {},
      current: { getDataSource: () => data },
    }),
    [reload, data, rowSelection]
  );

  useEffect(() => {
    if (actionRef && !isPC) {
      if (typeof actionRef === "function") {
        actionRef(mobileActionRef);
      } else if (actionRef.current !== undefined) {
        actionRef.current = mobileActionRef;
      }
    }
  }, [actionRef, isPC, mobileActionRef]);

  /* ---- Desktop ---- */
  if (isPC) {
    return (
      <ProTable
        request={request}
        columns={columns}
        rowKey={rowKey}
        rowSelection={rowSelection}
        pagination={pagination}
        expandable={expandable}
        onRow={onRow}
        search={search}
        toolBarRender={toolBarRender}
        options={options}
        actionRef={actionRef}
        postData={postData}
        {...rest}
      />
    );
  }

  /* ---- Mobile ---- */
  const mobilePagination = pagination === false
    ? false
    : { current: currentPage, pageSize, total, onChange: changePage, showSizeChanger: false };

  const mobileSortButton = mobileSortableColumns.length > 0 ? (
    <>
      <Tooltip title="Sort">
        <Button
          type="default"
          shape="circle"
          className={[
            "table-page-header-icon-btn",
            hasActiveMobileSort ? "table-page-header-icon-btn--sorted" : "",
          ].filter(Boolean).join(" ")}
          icon={<SortAscendingOutlined className="table-page-header-sort-icon" />}
          onClick={() => setMobileSortDrawerOpen(true)}
          aria-label="Sort"
        />
      </Tooltip>
      <Drawer
        open={mobileSortDrawerOpen}
        title="Sort"
        placement="bottom"
        height="72vh"
        className="responsive-picker-drawer mobile-sort-drawer"
        onClose={() => setMobileSortDrawerOpen(false)}
        destroyOnClose
      >
        <div className="responsive-picker-body">
          <div className="mobile-sort-reset-row">
            <Button type="link" onClick={handleMobileSortReset} disabled={!hasActiveMobileSort}>
              Reset
            </Button>
          </div>
          {mobileSortableColumns.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            mobileSortableColumns.flatMap((col) => [
              { ...col, order: "ascend", direction: "Ascending" },
              { ...col, order: "descend", direction: "Descending" },
            ]).map((option) => {
              const selected =
                activeMobileSortField === option.field &&
                activeMobileSortOrder === option.order;
              return (
                <div
                  key={`${option.field}:${option.order}`}
                  className="responsive-picker-option mobile-sort-option"
                  onClick={() => handleMobileSortSelect(option.field, option.order)}
                >
                  <span className="responsive-picker-option-label">
                    {option.label} {option.direction}
                  </span>
                  {selected ? <CheckOutlined className="responsive-picker-option-check" /> : null}
                </div>
              );
            })
          )}
        </div>
      </Drawer>
    </>
  ) : null;

  const topToolbar =
    typeof toolBarRender === "function"
      ? toolBarRender({ mobile: true, mobileSortSlot: mobileSortButton })
      : toolBarRender;

  return (
    <MobileCardList
      columns={mobileColumns}
      dataSource={data}
      loading={loading}
      rowKey={rowKey || "id"}
      rowSelection={rowSelection}
      pagination={mobilePagination}
      expandable={expandable}
      onRow={onRow}
      onRefresh={refresh}
      mobileRowActions={mobileRowActions}
      mobileDetailButtonText={mobileDetailButtonText}
      hideMobileColumnActions={hideMobileColumnActions}
      hideMobileTableExpand={hideMobileTableExpand}
      mobileCardHeaderRender={mobileCardHeaderRender}
      mobileTitleOnClick={mobileTitleOnClick}
      mobileExpandedExcludeKeys={mobileExpandedExcludeKeys}
      mobileExpandedIncludeKeys={mobileExpandedIncludeKeys}
      mobileInfiniteScroll={mobileInfiniteScroll}
      onLoadMore={loadMore}
      hasMore={hasMore}
      loadingMore={loadingMore}
      loadMoreError={loadMoreError}
      topToolbar={topToolbar}
      tableAction={mobileActionRef}
      size="small"
      {...rest}
    />
  );
};

export default ResponsiveProTable;
